import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Crown, 
  Sparkles, 
  Clock, 
  ArrowRight, 
  X,
  Gift,
  Zap
} from 'lucide-react';

/**
 * Floating Action Button for Subscription
 * Shows when user has no membership or membership is expiring
 */
export const SubscriptionFAB = ({ member, membershipPlan, onNavigate }) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const hasNoMembership = !membershipPlan;
  const isExpiringSoon = member?.membership_end_date && 
    new Date(member.membership_end_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const isExpired = member?.membership_status === 'expired' || member?.membership_status === 'cancelled';

  const shouldShow = !dismissed && (hasNoMembership || isExpiringSoon || isExpired);

  useEffect(() => {
    // Animate in after a delay
    if (shouldShow) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [shouldShow]);

  if (!shouldShow) return null;

  const getButtonConfig = () => {
    if (hasNoMembership || isExpired) {
      return {
        text: t('subscription.getStarted'),
        icon: Sparkles,
        className: 'bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600'
      };
    }
    if (isExpiringSoon) {
      return {
        text: t('subscription.renewNow'),
        icon: Clock,
        className: 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600'
      };
    }
    return null;
  };

  const config = getButtonConfig();
  if (!config) return null;

  const Icon = config.icon;

  return (
    <div 
      className={`fixed bottom-20 right-4 z-50 transition-all duration-500 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
      }`}
    >
      <div className="relative">
        {/* Dismiss button */}
        <button
          onClick={() => setDismissed(true)}
          className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-1 hover:bg-gray-700 transition-colors shadow-lg"
        >
          <X className="h-3 w-3" />
        </button>
        
        {/* Main FAB */}
        <Button
          onClick={() => onNavigate('membership')}
          className={`${config.className} text-white shadow-2xl rounded-full px-5 py-6 flex items-center gap-2 animate-pulse hover:animate-none`}
        >
          <Icon className="h-5 w-5" />
          <span className="font-semibold">{config.text}</span>
        </Button>
      </div>
    </div>
  );
};

/**
 * Header Banner for subscription alerts
 */
export const SubscriptionBanner = ({ member, membershipPlan, onNavigate }) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  const hasNoMembership = !membershipPlan;
  const isExpiringSoon = member?.membership_end_date && 
    new Date(member.membership_end_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const daysUntilExpiry = member?.membership_end_date 
    ? Math.ceil((new Date(member.membership_end_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const isExpired = member?.membership_status === 'expired' || member?.membership_status === 'cancelled';

  if (dismissed) return null;

  // No membership banner
  if (hasNoMembership) {
    return (
      <div className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-3 sm:px-4 py-2 sm:py-3">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-center sm:text-left">
            <Gift className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 hidden sm:block" />
            <span className="text-xs sm:text-sm font-medium">
              🎉 {t('subscription.activateMembershipToday')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => onNavigate('membership')}
              className="bg-white text-teal-600 hover:bg-gray-100 text-xs sm:text-sm h-7 sm:h-8 px-3"
            >
              {t('subscription.viewPlans')}
              <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
            </Button>
            <button 
              onClick={() => setDismissed(true)}
              className="text-white/80 hover:text-white p-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expired membership banner
  if (isExpired) {
    return (
      <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white px-3 sm:px-4 py-2 sm:py-3">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-center sm:text-left">
            <Clock className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 hidden sm:block" />
            <span className="text-xs sm:text-sm font-medium">
              {t('subscription.membershipExpired')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => onNavigate('membership')}
              className="bg-white text-red-600 hover:bg-gray-100 text-xs sm:text-sm h-7 sm:h-8 px-3"
            >
              {t('subscription.renewNow')}
              <Zap className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
            </Button>
            <button 
              onClick={() => setDismissed(true)}
              className="text-white/80 hover:text-white p-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expiring soon banner
  if (isExpiringSoon && daysUntilExpiry > 0) {
    return (
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 sm:px-4 py-2 sm:py-3">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-center sm:text-left">
            <Clock className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 hidden sm:block" />
            <span className="text-xs sm:text-sm font-medium">
              ⏰ {t('subscription.membershipExpiring', { count: daysUntilExpiry, days: daysUntilExpiry })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => onNavigate('membership')}
              className="bg-white text-orange-600 hover:bg-gray-100 text-xs sm:text-sm h-7 sm:h-8 px-3"
            >
              {t('subscription.renewNow')}
              <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
            </Button>
            <button 
              onClick={() => setDismissed(true)}
              className="text-white/80 hover:text-white p-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

/**
 * Prominent CTA Card for Overview/Dashboard
 */
export const SubscriptionCTACard = ({ member, membershipPlan, onNavigate }) => {
  const { t } = useTranslation();
  const hasNoMembership = !membershipPlan;
  const isExpiringSoon = member?.membership_end_date && 
    new Date(member.membership_end_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const daysUntilExpiry = member?.membership_end_date 
    ? Math.ceil((new Date(member.membership_end_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const isExpired = member?.membership_status === 'expired' || member?.membership_status === 'cancelled';

  // No membership CTA
  if (hasNoMembership) {
    return (
      <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 via-white to-emerald-50 overflow-hidden">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
              <Crown className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                {t('subscription.startYourJourney')}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {t('subscription.unlockBenefits')}
              </p>
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                <Badge className="bg-teal-100 text-teal-700">✓ {t('subscription.priorityBooking')}</Badge>
                <Badge className="bg-teal-100 text-teal-700">✓ {t('subscription.exclusiveDiscounts')}</Badge>
                <Badge className="bg-teal-100 text-teal-700">✓ {t('subscription.specialBenefits')}</Badge>
              </div>
            </div>
            <Button
              onClick={() => onNavigate('membership')}
              size="lg"
              className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto"
            >
              <Sparkles className="h-5 w-5 mr-2" />
              {t('subscription.viewPlans')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Expired CTA
  if (isExpired) {
    return (
      <Card className="border-2 border-red-200 bg-gradient-to-br from-red-50 via-white to-rose-50 overflow-hidden">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center flex-shrink-0">
              <Clock className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                {t('subscription.membershipExpiredTitle')}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {t('subscription.renewToEnjoy')}
              </p>
            </div>
            <Button
              onClick={() => onNavigate('membership')}
              size="lg"
              className="bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white shadow-lg w-full sm:w-auto"
            >
              <Zap className="h-5 w-5 mr-2" />
              {t('subscription.renewNow')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Expiring soon CTA
  if (isExpiringSoon && daysUntilExpiry > 0) {
    return (
      <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 overflow-hidden">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 relative">
              <Clock className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                {daysUntilExpiry}
              </span>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                {t('subscription.membershipExpiringSoon')}
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                {t('subscription.expiresInDays', { count: daysUntilExpiry, days: daysUntilExpiry })}
              </p>
            </div>
            <Button
              onClick={() => onNavigate('membership')}
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg w-full sm:w-auto"
            >
              <ArrowRight className="h-5 w-5 mr-2" />
              {t('subscription.renewNow')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default { SubscriptionFAB, SubscriptionBanner, SubscriptionCTACard };
